import { RSAA } from 'redux-api-middleware'
import { join, map, pipe, prop, uniq } from 'ramda'

import { types, initialState } from './index'
import { contentfulEndpoint, getContentfulProxyHeaders } from './utils'

// Contentful doesn't always fire an error when there is a problem.
// They'll still fire a 200 but with errors in the payload.
// We'll need to pull those out and store them if we want any chance
// at finding out there is a problem.
// This fn pulls the 'message' prop out of each of the error objects
// and then uses 'uniq' to dedupe the messages and join to make it a string.
const getErrorMessages = pipe(map(prop('message')), uniq(), join(' '))

/**
 * Generic action to fetch contentful data that requires a graphql query Meta
 * object is optional
 *
 * @param {String} query The graphql query, should be passed in to the endpoint
 *   from the the component thats calling this action
 * @param {Object} meta Only needed for care search contenful calls
 *   insuranceProduct, searchTerms, ratingArea used for care middleware
 *   prechecked providers & heap tracking
 */
export const fetchContentfulData = (query, meta = {}) => {
  return {
    [RSAA]: {
      endpoint: contentfulEndpoint,
      method: 'POST',
      headers: getContentfulProxyHeaders(),
      body: JSON.stringify({ query }),
      types: [
        types.FETCH_CONTENTFUL_DATA_REQUEST,
        {
          type: types.FETCH_CONTENTFUL_DATA_SUCCESS,
          // metadata for care middleware prechecked providers & heap tracking
          meta,
        },
        types.FETCH_CONTENTFUL_DATA_ERROR,
      ],
      cache: true,
    },
  }
}

/**
 * Care Search fetch contentful data that requires a graphql query Meta object
 * is optional, only needed for care search contenful calls
 *
 * @param {String} query The graphql query, should be passed in to the endpoint
 *   from the the component thats calling this action
 * @param {Object} meta Only needed for care search contenful calls
 *   insuranceProduct, searchTerms, ratingArea used for care middleware
 *   prechecked providers & heap tracking
 */
export const fetchCareContentfulData = (query, meta = {}) => {
  return {
    [RSAA]: {
      endpoint: contentfulEndpoint,
      method: 'POST',
      headers: getContentfulProxyHeaders(),
      body: JSON.stringify({ query }),
      types: [
        types.FETCH_CARE_SEARCH_DATA_REQUEST,
        {
          type: types.FETCH_CARE_SEARCH_DATA_SUCCESS,
          // metadata for care middleware prechecked providers & heap tracking
          meta,
        },
        types.FETCH_CARE_SEARCH_DATA_ERROR,
      ],
      cache: true,
    },
  }
}

/**
 * Help center fetch contentful data that requires a graphql query
 *
 * @param {String} query The graphql query, should be passed in to the endpoint
 *   from the the component thats calling this action
 */
export const fetchHelpCategories = query => {
  return {
    [RSAA]: {
      endpoint: contentfulEndpoint,
      method: 'POST',
      headers: getContentfulProxyHeaders(),
      body: JSON.stringify({ query }),
      types: [
        types.FETCH_HELP_CATEGORIES_DATA_REQUEST,
        types.FETCH_HELP_CATEGORIES_DATA_SUCCESS,
        types.FETCH_HELP_CATEGORIES_DATA_ERROR,
      ],
      cache: true,
    },
  }
}

/**
 * Help center fetch contentful data that requires a graphql query
 *
 * @param {String} query The graphql query, should be passed in to the endpoint
 *   from the the component thats calling this action
 */
export const fetchVideos = query => {
  return {
    [RSAA]: {
      endpoint: contentfulEndpoint,
      method: 'POST',
      headers: getContentfulProxyHeaders(),
      body: JSON.stringify({ query }),
      types: [
        types.FETCH_VIDEOS_DATA_REQUEST,
        types.FETCH_VIDEOS_DATA_SUCCESS,
        types.FETCH_VIDEOS_DATA_ERROR,
      ],
      cache: true,
    },
  }
}

/**
 * Help center fetch contentful data that requires a graphql query
 *
 * @param {String} query The graphql query, should be passed in to the endpoint
 *   from the the component thats calling this action
 */
export const fetchFeaturedArticles = query => {
  return {
    [RSAA]: {
      endpoint: contentfulEndpoint,
      method: 'POST',
      headers: getContentfulProxyHeaders(),
      body: JSON.stringify({ query }),
      types: [
        types.FETCH_FEATURED_ARTICLES_DATA_REQUEST,
        types.FETCH_FEATURED_ARTICLES_DATA_SUCCESS,
        types.FETCH_FEATURED_ARTICLES_DATA_ERROR,
      ],
      cache: true,
    },
  }
}

/**
 * Help center fetch contentful data that requires a graphql query
 *
 * @param {String} query The graphql query, should be passed in to the endpoint
 *   from the the component thats calling this action
 */
export const fetchFeaturedVideos = query => {
  return {
    [RSAA]: {
      endpoint: contentfulEndpoint,
      method: 'POST',
      headers: getContentfulProxyHeaders(),
      body: JSON.stringify({ query }),
      types: [
        types.FETCH_FEATURED_VIDEOS_DATA_REQUEST,
        types.FETCH_FEATURED_VIDEOS_DATA_SUCCESS,
        types.FETCH_FEATURED_VIDEOS_DATA_ERROR,
      ],
      cache: true,
    },
  }
}

/**
 * Help center fetch contentful data that requires a graphql query
 *
 * @param {String} query The graphql query, should be passed in to the endpoint
 *   from the the component thats calling this action
 */
export const fetchRelatedArticles = query => {
  return {
    [RSAA]: {
      endpoint: contentfulEndpoint,
      method: 'POST',
      headers: getContentfulProxyHeaders(),
      body: JSON.stringify({ query }),
      types: [
        types.FETCH_RELATED_ARTICLES_DATA_REQUEST,
        types.FETCH_RELATED_ARTICLES_DATA_SUCCESS,
        types.FETCH_RELATED_ARTICLES_DATA_ERROR,
      ],
      cache: true,
    },
  }
}

/**
 * Dashboard fetch contentful data that requires a graphql query
 *
 * @param {String} query The graphql query, should be passed in to the endpoint
 *   from the the component thats calling this action
 */
export const fetchAnnouncementsData = query => {
  return {
    [RSAA]: {
      endpoint: contentfulEndpoint,
      method: 'POST',
      headers: getContentfulProxyHeaders(),
      body: JSON.stringify({ query }),
      types: [
        types.FETCH_ANNOUNCEMENTS_DATA_REQUEST,
        types.FETCH_ANNOUNCEMENTS_DATA_SUCCESS,
        types.FETCH_ANNOUNCEMENTS_DATA_ERROR,
      ],
      cache: true,
    },
  }
}

//-- Clear Contentful consts
export const clearContentfulData = () => ({
  type: types.CLEAR_CONTENTFUL_DATA,
})

export const clearCareContentfulData = () => ({
  type: types.CLEAR_CARE_CONTENTFUL_DATA,
})

export const clearHelpCategories = () => ({
  type: types.CLEAR_HELP_CATEGORIES_DATA,
})

export const clearVideosData = () => ({
  type: types.CLEAR_FEATURED_VIDEOS_DATA,
})

export const clearFeaturedArticles = () => ({
  type: types.CLEAR_FEATURED_ARTICLES_DATA,
})

export const clearFeaturedVideosData = () => ({
  type: types.CLEAR_FEATURED_VIDEOS_DATA,
})

export const clearRelatedArticlesData = () => ({
  type: types.CLEAR_RELATED_ARTICLES_DATA,
})

export const clearAnnouncementsData = () => ({
  type: types.CLEAR_ANNOUNCEMENTS_DATA,
})

let CONTENTFUL_REDUCER_LOOKUP = null
const getContentfulReducerLookup = () => {
  if (!CONTENTFUL_REDUCER_LOOKUP) {
    CONTENTFUL_REDUCER_LOOKUP = {
      // -- Generic Contentful store
      [types.FETCH_CONTENTFUL_DATA_REQUEST]: state => ({
        ...state,
        contentful: { ...state.contentful, busy: true, error: null },
      }),
      [types.FETCH_CONTENTFUL_DATA_SUCCESS]: (state, payload) => {
        const { data, errors = [] } = payload
        return {
          ...state,
          contentful: {
            ...state.contentful,
            busy: false,
            entities: data,
            error: getErrorMessages(errors),
          },
        }
      },
      [types.FETCH_CONTENTFUL_DATA_ERROR]: (state, payload) => ({
        ...state,
        contentful: { ...state.contentful, busy: false, error: payload },
      }),
      [types.CLEAR_CONTENTFUL_DATA]: state => ({
        ...state,
        contentful: {
          ...state.contentful,
          entities: initialState.contentful.entities,
        },
      }),

      // -- Care search Contentful store
      [types.FETCH_CARE_SEARCH_DATA_REQUEST]: state => ({
        ...state,
        careSearch: { ...state.careSearch, busy: true, error: null },
      }),
      [types.FETCH_CARE_SEARCH_DATA_SUCCESS]: (state, payload) => {
        const { data, errors = [] } = payload
        return {
          ...state,
          careSearch: {
            ...state.careSearch,
            busy: false,
            entities: data,
            error: getErrorMessages(errors),
          },
        }
      },
      [types.FETCH_CARE_SEARCH_DATA_ERROR]: (state, payload) => ({
        ...state,
        careSearch: { ...state.careSearch, busy: false, error: payload },
      }),
      [types.CLEAR_CARE_CONTENTFUL_DATA]: state => ({
        ...state,
        careSearch: {
          ...state.careSearch,
          entities: initialState.careSearch.entities,
        },
      }),

      // -- Help categories Contentful store
      [types.FETCH_HELP_CATEGORIES_DATA_REQUEST]: state => ({
        ...state,
        helpCategories: { ...state.helpCategories, busy: true, error: null },
      }),
      [types.FETCH_HELP_CATEGORIES_DATA_SUCCESS]: (state, payload) => {
        const { data, errors = [] } = payload
        return {
          ...state,
          helpCategories: {
            ...state.helpCategories,
            busy: false,
            entities: data,
            error: getErrorMessages(errors),
          },
        }
      },
      [types.FETCH_HELP_CATEGORIES_DATA_ERROR]: (state, payload) => ({
        ...state,
        helpCategories: {
          ...state.helpCategories,
          busy: false,
          error: payload,
        },
      }),
      [types.CLEAR_HELP_CATEGORIES_DATA]: state => ({
        ...state,
        helpCategories: {
          ...state.helpCategories,
          entities: initialState.helpCategories.entities,
        },
      }),

      // -- Videos Contentful store
      [types.FETCH_VIDEOS_DATA_REQUEST]: state => ({
        ...state,
        videos: { ...state.videos, busy: true, error: null },
      }),
      [types.FETCH_VIDEOS_DATA_SUCCESS]: (state, payload) => {
        const { data, errors = [] } = payload
        return {
          ...state,
          videos: {
            ...state.videos,
            busy: false,
            entities: data,
            error: getErrorMessages(errors),
          },
        }
      },
      [types.FETCH_VIDEOS_DATA_ERROR]: (state, payload) => ({
        ...state,
        videos: { ...state.videos, busy: false, error: payload },
      }),
      [types.CLEAR_VIDEOS_DATA]: state => ({
        ...state,
        videos: { ...state.videos, entities: initialState.videos.entities },
      }),

      // -- Featured articles Contentful store
      [types.FETCH_FEATURED_ARTICLES_DATA_REQUEST]: state => ({
        ...state,
        featuredArticles: {
          ...state.featuredArticles,
          busy: true,
          error: null,
        },
      }),
      [types.FETCH_FEATURED_ARTICLES_DATA_SUCCESS]: (state, payload) => {
        const { data, errors = [] } = payload
        return {
          ...state,
          featuredArticles: {
            ...state.featuredArticles,
            busy: false,
            entities: data,
            error: getErrorMessages(errors),
          },
        }
      },
      [types.FETCH_FEATURED_ARTICLES_DATA_ERROR]: (state, payload) => ({
        ...state,
        featuredArticles: {
          ...state.featuredArticles,
          busy: false,
          error: payload,
        },
      }),
      [types.CLEAR_FEATURED_ARTICLES_DATA]: state => ({
        ...state,
        featuredArticles: {
          ...state.featuredArticles,
          entities: initialState.featuredArticles.entities,
        },
      }),

      // -- Featured videos Contentful store
      [types.FETCH_FEATURED_VIDEOS_DATA_REQUEST]: state => ({
        ...state,
        featuredVideos: { ...state.featuredVideos, busy: true, error: null },
      }),
      [types.FETCH_FEATURED_VIDEOS_DATA_SUCCESS]: (state, payload) => {
        const { data, errors = [] } = payload
        return {
          ...state,
          featuredVideos: {
            ...state.featuredVideos,
            busy: false,
            entities: data,
            error: getErrorMessages(errors),
          },
        }
      },
      [types.FETCH_FEATURED_VIDEOS_DATA_ERROR]: (state, payload) => ({
        ...state,
        featuredVideos: {
          ...state.featuredVideos,
          busy: false,
          error: payload,
        },
      }),
      [types.CLEAR_FEATURED_VIDEOS_DATA]: state => ({
        ...state,
        featuredVideos: {
          ...state.featuredVideos,
          entities: initialState.featuredVideos.entities,
        },
      }),

      // -- Related articles Contentful store
      [types.FETCH_RELATED_ARTICLES_DATA_REQUEST]: state => ({
        ...state,
        relatedArticles: { ...state.relatedArticles, busy: true, error: null },
      }),
      [types.FETCH_RELATED_ARTICLES_DATA_SUCCESS]: (state, payload) => {
        const { data, errors = [] } = payload
        return {
          ...state,
          relatedArticles: {
            ...state.relatedArticles,
            busy: false,
            entities: data,
            error: getErrorMessages(errors),
          },
        }
      },
      [types.FETCH_RELATED_ARTICLES_DATA_ERROR]: (state, payload) => ({
        ...state,
        relatedArticles: {
          ...state.relatedArticles,
          busy: false,
          error: payload,
        },
      }),
      [types.CLEAR_RELATED_ARTICLES_DATA]: state => ({
        ...state,
        relatedArticles: {
          ...state.relatedArticles,
          entities: initialState.relatedArticles.entities,
        },
      }),

      // -- Announcements Contentful store
      [types.FETCH_ANNOUNCEMENTS_DATA_REQUEST]: state => ({
        ...state,
        announcements: { ...state.announcements, busy: true, error: null },
      }),
      [types.FETCH_ANNOUNCEMENTS_DATA_SUCCESS]: (state, payload) => {
        const { data, errors = [] } = payload
        return {
          ...state,
          announcements: {
            ...state.announcements,
            busy: false,
            entities: data,
            error: getErrorMessages(errors),
          },
        }
      },
      [types.FETCH_ANNOUNCEMENTS_DATA_ERROR]: (state, payload) => ({
        ...state,
        announcements: { ...state.announcements, busy: false, error: payload },
      }),
      [types.CLEAR_ANNOUNCEMENTS_DATA]: state => ({
        ...state,
        announcements: {
          ...state.announcements,
          entities: initialState.announcements.entities,
        },
      }),
    }
  }
  return CONTENTFUL_REDUCER_LOOKUP
}

export default (state = initialState, { type, payload = {}, meta = {} }) =>
  getContentfulReducerLookup()[type]?.(state, payload, meta) ?? state
